<template>
  <div id="property-detail-form">
    <v-card class="ma-0">
      <v-container>
        <v-card-title class="property-detail-form__title mb-5 mt-3">
          {{ editMode ? 'Edit Property' : 'Add Property' }}
        </v-card-title>
        <v-form v-model="valid">
          <v-col class="py-0">
            <fieldset>
              <v-container>
                <legend>
                  <v-icon
                    size="23"
                    color="#3C3333"
                  >
                    $faHome
                  </v-icon>
                  <h2 style="font-weight:400">
                    Property Information
                  </h2>
                </legend>
                <v-row class="mt-4">
                  <v-col class="py-0">
                    <v-text-field
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="Property Name"
                      maxlength="50"
                      required
                      :value="name"
                      :rules="[val => (!!val && !Number(val)) || 'Please enter a property name', val => (val.length > 0 && val.length <= 50) || 'Please try a shorter name']"
                      @input="val => this.$emit('update:name', val)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-0">
                    <v-select
                      class="prop-field"
                      color="primary"
                      append-icon="$faChevronDown"
                      dense
                      filled
                      required
                      label="Property Type"
                      :value="propertyType"
                      :items="['House', 'Apartment/Condo', 'Estate']"
                      :rules="[val => !!val || 'Please select a property type']"
                      @input="val => this.$emit('update:propertyType', val)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-0 pr-0 mr-4">
                    <v-text-field
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      type="number"
                      min="1"
                      label="Square Footage (Optional)"
                      :value="area ? area : null"
                      :rules="[val => (!val || !!Number(val)) || 'Please use numeric values', val => (!val || Number(val) >= 0) || 'Please enter a positive number']"
                      @input="val => this.$emit('update:area', Number.parseInt(val))"
                    >
                      <template v-slot:append>
                        <span id="sqft-label">SQ FT</span>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col class="py-0 pl-0">
                    <v-text-field
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      type="number"
                      min="1"
                      label="Maximum Occupancy (Optional)"
                      :value="maxOccupancy ? maxOccupancy : null"
                      :rules="[val => (!val || !!Number(val)) || 'Please use numeric values', val => (!val || Number(val) >= 0) || 'Please enter a positive number']"
                      @input="val => this.$emit('update:maxOccupancy', Number.parseInt(val))"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </fieldset>
          </v-col>
          <v-col class="py-0">
            <fieldset>
              <v-container>
                <legend>
                  <v-icon
                    size="20"
                    color="#3C3333"
                  >
                    $faMapMarkerAlt
                  </v-icon>
                  <h2 style="font-weight: 400">
                    Address
                  </h2>
                </legend>
                <v-row class="mt-4">
                  <v-col
                    class="py-0"
                    @click="$emit('update:country', '')"
                  >
                    <v-autocomplete
                      class="prop-field"
                      color="primary"
                      append-icon="$faChevronDown"
                      dense
                      filled
                      label="Country"
                      required
                      placeholder="Start typing your country to search"
                      :value="country"
                      :items="countries"
                      :rules="[val => !!val || 'Please select a country']"
                      @input="val => $emit('update:country', val) "
                    >
                      <template #item="{item, on, attrs}">
                        <v-list-item
                          v-bind="attrs"
                          class="d-flex align-center"
                          v-on="on"
                        >
                          <div
                            class="d-flex flex-column"
                            style="width:100%"
                          >
                            <div class="d-flex align-center">
                              <country-flag
                                :country="item.value.toLowerCase()"
                                size="small"
                              />
                              <span class="ml-2">{{ item.text }}</span>
                            </div>
                            <v-divider
                              v-if="item.text === 'United Kingdom'"
                              class="my-2"
                            />
                          </div>
                        </v-list-item>
                      </template>
                      <!-- eslint-disable-next-line -->
                      <template #selection="{item, index}">
                        <country-flag
                          :country="item.value.toLowerCase()"
                          size="small"
                        />
                        <span class="ml-2">{{ item.text }}</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="Address Line 1"
                      required
                      :value="street1"
                      :rules="[val => (!!val && !Number(val)) || `Please enter this property's address`]"
                      @input="val => this.$emit('update:street1', val)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="Address Line 2 (Optional)"
                      :value="street2"
                      @input="val => this.$emit('update:street2', val)"
                    />
                  </v-col>
                </v-row>
                <v-row class="mx-0">
                  <v-col class="py-0 px-0 mr-2">
                    <v-text-field
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="City"
                      required
                      :value="city"
                      :rules="[val => (!!val && !Number(val)) || 'Please enter a city']"
                      @input="val => this.$emit('update:city', val)"
                    />
                  </v-col>
                  <v-col class="py-0 pl-0 pr-0 mr-2">
                    <v-select
                      v-if="countriesWithRegions.includes(country)"
                      class="prop-field"
                      color="primary"
                      append-icon="$faChevronDown"
                      dense
                      filled
                      :label="country === 'USA' ? 'State' : 'Region'"
                      required
                      :value="region"
                      :items="regions"
                      :rules="[val => (!!val && !Number(val)) || 'Please select a region']"
                      @input="val => this.$emit('update:region', val)"
                    />
                    <v-text-field
                      v-else
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="Region"
                      required
                      :value="region"
                      :rules="[val => (!!val && !Number(val)) || 'Please enter a region']"
                      @input="val => this.$emit('update:region', val)"
                    />
                  </v-col>
                  <v-col class="py-0 pl-0 pr-0">
                    <v-text-field
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      required
                      label="Zip Code"
                      :value="postalcode"
                      :rules="[val => (!!val || 'Please enter a zip code')]"
                      @input="val => this.$emit('update:postalcode', val) "
                    />
                  </v-col>
                </v-row>
              </v-container>
            </fieldset>
          </v-col>
          <v-col
            cols="6"
            class="pt-0"
          >
            <fieldset>
              <v-container class="pt-0">
                <legend>
                  <v-icon
                    small
                    class="form-icon"
                  >
                    $faTag
                  </v-icon>
                  <span>Property Tags</span>
                </legend>
                <v-card-text class="pl-0">
                  Organize your properties using tags
                </v-card-text>
                <div
                  id="property-tags"
                  class="col-6 col-sm-8 pl-0 pt-0"
                >
                  <v-text-field
                    v-model="inputTags"
                    class="prop-field"
                    color="primary"
                    label="Add a tag (Optional)"
                    dense
                    filled
                    :hide-details="true"
                    @keyup.enter="stageInputTags"
                  />
                  <v-btn
                    class="square-btn"
                    color="primary"
                    @click="stageInputTags"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                  </v-btn>
                </div>
                <div class="tags">
                  <v-chip
                    v-for="(tagName, i) in tags"
                    :key="i"
                    class="tag"
                    text-color="#535353"
                    color="#9E9E9E"
                    outlined
                    close
                    @click:close="() => removeTagChip(tagName)"
                  >
                    {{ tagName }}
                  </v-chip>
                </div>
              </v-container>
            </fieldset>
          </v-col>
          <v-row>
            <v-col class="py-0">
              <div class="button-group">
                <v-btn
                  v-if="editMode && canEditProperty(propertyId)"
                  text
                  color="error"
                  @click="() => this.$emit('delete-property', propertyId)"
                >
                  Delete property
                </v-btn>
                <v-btn
                  color="secondary"
                  @click="onCancelForm"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="isLoading || !valid"
                  :loading="isLoading"
                  @click.prevent="savePropertyDetails"
                >
                  {{ editMode ? 'Save' : 'Create' }}
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { regions as regionData } from '@/i18n'
  import countryListIso3 from '../../helpers/country-list-iso3.json'
  import countryListIso2 from '../../helpers/country-list-iso2.json'
  import CountryFlag from 'vue-country-flag'

  export default {
    name: 'PropertyDetailsForm',
    components: { CountryFlag },
    props: {
      editMode: {
        type: Boolean,
        default: false,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      name: String,
      propertyId: String,
      propertyType: String,
      area: Number,
      maxOccupancy: Number,
      street1: String,
      street2: String,
      city: String,
      region: String,
      postalcode: String,
      country: {
        type: String,
        default: 'US',
      },
      tags: {
        type: Array,
        default () {
          return []
        },
      },
    },
    data () {
      return {
        valid: false,
        inputTags: '',
        countriesWithRegions: ['US', 'USA', 'CA', 'CAN'],
      }
    },
    computed: {
      ...mapGetters({
        canEditProperty: 'Permissions/canEditProperty',
      }),
      regions () {
        let alpha2 = this.country.toLowerCase()
        if (alpha2 === 'usa') alpha2 = 'us'
        else if (alpha2 === 'can') alpha2 = 'ca'
        if (regionData[alpha2] !== undefined) {
          return regionData[alpha2].states.map(({ name, alpha2 }) => ({
            text:
              name,
            value: alpha2,
          }))
        } else return regionData.us.states.map(({ name, alpha2 }) => ({ text: name, value: alpha2 }))
      },
      countries () {
        let countryList
        if (this.country === 'US' || this.country === 'CA') countryList = countryListIso2
        else countryList = countryListIso3
        return Object.values(countryList).map(({ name, code }) => ({ text: name, value: code }))
      },
    },
    methods: {
      stageInputTags () {
        const newTag = this.inputTags.trim()
        if (newTag) this.$emit('update:tags', [...new Set([...this.tags, newTag])])
        this.inputTags = ''
      },
      removeTagChip (name) {
        this.$emit('update:tags', this.tags.filter(tagName => tagName !== name))
      },
      onCancelForm () {
        this.stagedTags = [...this.tags]
        this.$emit('cancel-edit-property')
      },
      goBack () {
        this.$router.push(`/properties/${this.propertyId}`)
      },
      savePropertyDetails () {
        this.$emit('save-property-details')
      },
    },
  }
</script>

<style lang="scss">
  #property-detail-form {
    .v-select.v-input--dense .v-select__selection--comma {
      margin: 0 4px 10px 0;
    }
  }
  .property-detail-form__title {
    font-family: "Degular", sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 34px;
    line-height: 24px;
    letter-spacing: 0.7px;
    color: #535353;
  }
  #property-tags {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    flex-direction: row;
    & > * {
      margin-right: 6px;
    }
    .square-btn {
      margin-bottom: 0;
    }
  }
  #sqft-label {
    white-space:nowrap;
    font-size:16px;
    margin-top:7px;
  }
  fieldset {
    border: none;
    padding: 0;
    margin: 0;
  }
  legend {
    left: 20px;
    display: flex;
    align-items: center;
    .v-icon {
      margin-right: 11px;
    }
    span:last-child {
      font-size: 24px
    }
  }
  .button-group {
    text-align: right;
    margin-bottom: 20px;
  }
  // Turn off number spinner UI
    /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }

</style>
